import React, { useEffect } from "react";
import "./About.css";
import { useLocalContext } from "../../context/LocalContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faClock,
  faUserShield,
  faHeadset,
  faListCheck,
  faCircleCheck,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import WhyChooseUs from "../WhyChooseUs/WhyChooseUs";

const About = () => {
  const { webinfo } = useLocalContext();
  const navigate = useNavigate();
  const brand = webinfo?.name || "Our Security Store";

  useEffect(() => {
    document.title = `About | ${brand}`;
  }, [brand]);

  return (
    <>
      {/* HERO – brand story / positioning */}
      <section className="about about-hero" aria-labelledby="about-hero-heading">
        <div className="about-hero-inner">
          <div className="about-hero-copy">
            <span className="hero-tag">Get to know {brand}</span>
            <h1 id="about-hero-heading">
              Your<span>friendly security partner</span> for everyday devices.
            </h1>
            <p className="hero-lead">
              {brand} helps people who don’t live in IT departments stay safely
              online. We connect you with trusted security software, explain
              what each plan actually does, and guide you through activation
              without jargon or pressure.
            </p>

            <div className="hero-highlights" role="list">
              <span role="listitem">
                <FontAwesomeIcon icon={faShieldAlt} /> Focus on reputable
                security brands
              </span>
              <span role="listitem">
                <FontAwesomeIcon icon={faClock} /> Fast help when you’re ready
                to activate
              </span>
              <span role="listitem">
                <FontAwesomeIcon icon={faHeadset} /> Real humans, plain-language
                answers
              </span>
            </div>

            <div className="hero-actions">
              <button
                className="btn-primary"
                type="button"
                onClick={() => navigate("/shop")}
              >
                Browse protection plans
              </button>
              <button
                className="btn-ghost"
                type="button"
                onClick={() => navigate("/contact")}
              >
                Ask which plan is right for me
              </button>
            </div>
          </div>

          <div className="about-hero-facts" aria-label="Quick facts about our service">
            <div className="fact-card">
              <p className="fact-label">Who we help</p>
              <p className="fact-value">
                Individuals, families &amp; small businesses who want{" "}
                <strong>simple, reliable protection</strong> without a full-time
                IT team.
              </p>
            </div>
            <div className="fact-card">
              <p className="fact-label">How we show up</p>
              <p className="fact-value">
                Clear explanations, policy-safe guidance, and honest expectations
                about what your software can and can’t do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION – why we exist */}
      <section
        className="about section mission"
        aria-labelledby="about-mission-heading"
      >
        <header className="section-head">
          <h2 id="about-mission-heading">
            <FontAwesomeIcon icon={faListCheck} /> Our mission
          </h2>
          <p>
            Staying safe online shouldn’t feel like a full-time job. Our mission
            is to make modern security tools feel as approachable as any other
            household or business service.
          </p>
        </header>

        <div className="pill-grid">
          <div className="pill">
            <FontAwesomeIcon icon={faUserShield} />
            Turn “I’m not technical” into “I know what I’m using”
          </div>
          <div className="pill">
            <FontAwesomeIcon icon={faClock} />
            Reduce setup time from hours of guessing to minutes of guidance
          </div>
          <div className="pill">
            <FontAwesomeIcon icon={faLock} />
            Encourage practical habits, not fear-based messaging
          </div>
          <div className="pill">
            <FontAwesomeIcon icon={faHeadset} />
            Stay reachable for simple, human support when you need it
          </div>
        </div>
      </section>

      {/* HOW WE HELP – service “buckets” */}
      <section
        className="about section offer"
        aria-labelledby="about-offer-heading"
      >
        <header className="section-head">
          <h2 id="about-offer-heading">How we help you stay protected</h2>
          <p>
            We don’t build the antivirus engines—our role is to help you{" "}
            understand, choose, and use them with confidence.
          </p>
        </header>

        <div className="card-grid">
          <article className="card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faShieldAlt} />
            </div>
            <h3>Plan clarity</h3>
            <p>
              We explain the difference between basic antivirus, full “internet
              security” suites, and all-in-one bundles so you’re clear about
              what you’re buying.
            </p>
          </article>

          <article className="card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <h3>Faster setup</h3>
            <p>
              From email delivery of your license key to activation steps, we
              keep instructions focused on what you actually need to click and
              where.
            </p>
          </article>

          <article className="card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faUserShield} />
            </div>
            <h3>Multi-device advice</h3>
            <p>
              Need to cover laptops, phones, or shared family devices? We help
              you match plans with supported platforms and device limits.
            </p>
          </article>

          <article className="card">
            <div className="card-icon">
              <FontAwesomeIcon icon={faHeadset} />
            </div>
            <h3>Support with context</h3>
            <p>
              We can’t replace vendor support desks, but we can help you
              understand what’s happening and point you to the right next step.
            </p>
          </article>
        </div>
      </section>

      {/* POLICY & TRANSPARENCY – kept but reworded a bit */}
      <section
        className="about section policy"
        aria-labelledby="about-policy-heading"
      >
        <header className="section-head">
          <h2 id="about-policy-heading">Transparency & important notes</h2>
          <p>
            We want you to know exactly who you’re buying from and how these
            security products fit into your digital life.
          </p>
        </header>

        <ul className="list">
          <li>
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>
              <strong>Independent reseller:</strong> {brand} is not the
              manufacturer, publisher, or official representative of the
              software brands shown. All product names, logos, and trademarks
              belong to their respective owners and are used only for
              identification and description.
            </span>
          </li>
          <li>
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>
              <strong>Plan differences:</strong> Features (like VPN, cloud
              backup, password managers, and storage limits) can vary by plan,
              operating system, and region. Please review the vendor&apos;s
              documentation and terms for full details.
            </span>
          </li>
          <li>
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>
              <strong>Support & warranties:</strong> Technical support, app
              updates, and any product warranties are provided directly by the
              original software provider as described in their policies. Our
              role is to assist with purchase, license delivery, and general
              activation steps.
            </span>
          </li>
          <li>
            <FontAwesomeIcon icon={faCircleCheck} />
            <span>
              <strong>Your agreement:</strong> By purchasing through {brand},
              you agree to our{" "}
              <button
                className="linklike"
                type="button"
                onClick={() => navigate("/terms-and-conditions")}
              >
                Terms &amp; Conditions
              </button>{" "}
              and{" "}
              <button
                className="linklike"
                type="button"
                onClick={() => navigate("/privacy-policy")}
              >
                Privacy Policy
              </button>
              . For refunds and delivery details, please review our{" "}
              <button
                className="linklike"
                type="button"
                onClick={() => navigate("/refund-policy")}
              >
                Refund Policy
              </button>{" "}
              and{" "}
              <button
                className="linklike"
                type="button"
                onClick={() => navigate("/shipping-policy")}
              >
                Shipping &amp; Delivery
              </button>
              .
            </span>
          </li>
        </ul>
      </section>

      {/* CTA – simple text-only closer */}
      <section
        className="about section cta"
        aria-labelledby="about-cta-heading"
      >
        <h3 id="about-cta-heading">Ready to feel more secure online?</h3>
        <p>
          Tell us what devices you use and what worries you the most—payments,
          kids browsing, work data—and we’ll suggest a plan that fits.
        </p>
        <div className="cta-actions">
          <button
            className="btn-primary"
            type="button"
            onClick={() => navigate("/shop")}
          >
            See available plans
          </button>
          <button
            className="btn-ghost"
            type="button"
            onClick={() => navigate("/contact")}
          >
            Talk to our team
          </button>
        </div>
      </section>

      {/* Extra reassurance / benefits section (your existing component) */}
      <WhyChooseUs />
    </>
  );
};

export default About;
